import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

type Tone = 'neutral' | 'primary' | 'info' | 'success' | 'warning' | 'danger';

type AdminPageHeaderProps = {
  title: string;
  description?: string;
  eyebrow?: string;
  actions?: React.ReactNode;
};

type AdminStatCardProps = {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  helper?: React.ReactNode;
  tone?: Tone;
};

type AdminFilterCardProps = {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
};

type AdminTableCardProps = {
  title?: string;
  description?: string;
  meta?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
};

type AdminEmptyStateProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
};

type AdminOverlayShellProps = {
  title: string;
  description?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidthClassName?: string;
  panelClassName?: string;
  bodyClassName?: string;
};

const toneClassNames: Record<Tone, string> = {
  neutral: 'text-on-surface-variant',
  primary: 'text-primary',
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-error',
};

export function AdminPageHeader({ title, description, eyebrow, actions }: AdminPageHeaderProps) {
  return (
    <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
      <div>
        {eyebrow ? (
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-on-surface-variant">{eyebrow}</p>
        ) : null}
        <h1 className="text-3xl font-bold tracking-[-0.03em] text-on-surface">{title}</h1>
        {description ? (
          <p className="mt-2 max-w-3xl text-sm leading-6 text-on-surface-variant">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function AdminStatGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">{children}</div>;
}

export function AdminStatCard({ icon, label, value, helper, tone = 'primary' }: AdminStatCardProps) {
  return (
    <div className="admin-subpanel rounded-card p-4">
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 ${toneClassNames[tone]}`}>{icon}</div>
        <div className="min-w-0">
          <p className="text-sm text-on-surface-variant">{label}</p>
          <p className="mt-1 text-2xl font-bold tracking-[-0.03em] text-on-surface">{value}</p>
          {helper ? <div className="mt-1 text-xs leading-5 text-on-surface-variant">{helper}</div> : null}
        </div>
      </div>
    </div>
  );
}

export function AdminFilterCard({ title, description, icon, actions, children, className = '' }: AdminFilterCardProps) {
  return (
    <div className={`admin-subpanel rounded-card p-4 ${className}`.trim()}>
      {title || description || icon || actions ? (
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            {title ? (
              <div className="flex items-center gap-2">
                {icon ? <span className="text-on-surface-variant">{icon}</span> : null}
                <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-on-surface">{title}</h2>
              </div>
            ) : null}
            {description ? <p className="mt-2 text-sm leading-6 text-on-surface-variant">{description}</p> : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

export function AdminTableCard({ title, description, meta, children, className = '' }: AdminTableCardProps) {
  return (
    <div className={`admin-table-card ${className}`.trim()}>
      {title || description || meta ? (
        <div className="admin-table-toolbar px-4 py-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              {title ? <h2 className="text-lg font-semibold text-on-surface">{title}</h2> : null}
              {description ? <p className="mt-1 text-sm leading-6 text-on-surface-variant">{description}</p> : null}
            </div>
            {meta ? <div className="text-sm text-on-surface-variant">{meta}</div> : null}
          </div>
        </div>
      ) : null}
      {children}
    </div>
  );
}

export function AdminEmptyState({ icon, title, description, action }: AdminEmptyStateProps) {
  return (
    <div className="px-6 py-12 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl admin-avatar-tile text-on-surface-variant">
        {icon}
      </div>
      <h3 className="mt-4 text-base font-semibold text-on-surface">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-on-surface-variant">{description}</p>
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </div>
  );
}

export function AdminModalShell({
  title,
  description,
  onClose,
  children,
  footer,
  maxWidthClassName = 'max-w-3xl',
  panelClassName = '',
  bodyClassName = '',
}: AdminOverlayShellProps) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/55 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="flex min-h-full items-start justify-center py-4 sm:items-center">
        <div
          className={`admin-table-card flex w-full flex-col ${maxWidthClassName} max-h-[calc(100vh-2rem)] sm:max-h-[90vh] ${panelClassName}`.trim()}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="admin-table-toolbar sticky top-0 z-[1] shrink-0 px-6 py-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-on-surface">{title}</h2>
                {description ? <p className="mt-1 text-sm leading-6 text-on-surface-variant">{description}</p> : null}
              </div>
              <button type="button" className="admin-icon-button rounded-xl p-2" onClick={onClose}>
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className={`min-h-0 flex-1 overflow-y-auto p-6 ${bodyClassName}`.trim()}>{children}</div>
          {footer ? <div className="admin-table-toolbar shrink-0 px-6 py-4">{footer}</div> : null}
        </div>
      </div>
    </div>
  );
}

export function AdminDrawerShell({
  title,
  description,
  onClose,
  children,
  footer,
  maxWidthClassName = 'max-w-xl',
  panelClassName = '',
  bodyClassName = '',
}: AdminOverlayShellProps) {
  return (
    <div className="fixed inset-0 z-50 bg-black/35 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`admin-shell-surface absolute inset-y-0 right-0 w-full ${maxWidthClassName} overflow-y-auto border-l border-outline/60 ${panelClassName}`.trim()}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="admin-table-toolbar sticky top-0 z-[1] px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-on-surface">{title}</h2>
              {description ? <p className="mt-1 text-sm leading-6 text-on-surface-variant">{description}</p> : null}
            </div>
            <button type="button" className="admin-icon-button rounded-xl p-2" onClick={onClose}>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className={`px-6 py-5 ${bodyClassName}`.trim()}>{children}</div>
        {footer ? <div className="admin-table-toolbar px-6 py-4">{footer}</div> : null}
      </div>
    </div>
  );
}